import { toast } from 'sonner';

export interface OrderItem {
  name: string;
  quantity: number;
  price: number;
}

export interface Order {
  id: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  items: OrderItem[];
  total: number;
  status: 'pending' | 'preparing' | 'ready' | 'completed';
  timestamp: string;
  paymentStatus: 'paid' | 'pending';
}

const ORDERS_KEY = 'kpc_orders';
const API_URL = '/api/checkout';

// Local storage functions (fallback)
function saveToLocal(orders: Order[]): void {
  try {
    localStorage.setItem(ORDERS_KEY, JSON.stringify(orders));
  } catch (error) {
    console.error('Error saving to localStorage:', error);
  }
}

function getFromLocal(): Order[] {
  try {
    const data = localStorage.getItem(ORDERS_KEY);
    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
}

// Fetch orders from SERVER (shared across all devices)
export async function fetchOrdersFromServer(): Promise<Order[]> {
  try {
    const response = await fetch(API_URL, { method: 'GET' });
    if (!response.ok) throw new Error('Failed to fetch');
    const data = await response.json();
    const serverOrders = data.orders || [];
    // Merge with local orders (local changes take priority for status updates)
    const localOrders = getFromLocal();
    const merged = mergeOrders(localOrders, serverOrders);
    saveToLocal(merged);
    return merged;
  } catch (error) {
    console.error('Server fetch failed, using local:', error);
    return getFromLocal();
  }
}

// Merge orders: keep the most recent status from either source
function mergeOrders(local: Order[], server: Order[]): Order[] {
  const orderMap = new Map<string, Order>();
  // Add all local orders first
  local.forEach(o => orderMap.set(o.id, o));
  // Merge server orders (server wins if newer)
  server.forEach(o => {
    const existing = orderMap.get(o.id);
    if (!existing) {
      orderMap.set(o.id, o);
    }
    // Keep local status if it was manually updated (not pending)
    // Server status is used as base
  });
  return Array.from(orderMap.values()).sort((a, b) =>
    new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );
}

// Save order to localStorage (client-side, after payment)
export function saveOrder(order: Order): void {
  try {
    const existing = getFromLocal();
    existing.unshift(order);
    saveToLocal(existing);
  } catch (error) {
    console.error('Error saving order:', error);
  }
}

// Get orders (from localStorage - used by components)
export function getOrders(): Order[] {
  return getFromLocal();
}

// Update order status (local + try server)
export function updateOrderStatus(orderId: string, status: Order['status']): void {
  try {
    const orders = getFromLocal();
    const index = orders.findIndex(o => o.id === orderId);
    if (index !== -1) {
      orders[index].status = status;
      saveToLocal(orders);
      toast.success(`Order ${orderId} marked as ${status}`);
    }
  } catch (error) {
    console.error('Error updating order:', error);
  }
}

export function clearCompletedOrders(): void {
  try {
    const orders = getFromLocal().filter(o => o.status !== 'completed');
    saveToLocal(orders);
    toast.success('Completed orders cleared');
  } catch (error) {
    console.error('Error clearing orders:', error);
  }
}

export function getPendingOrdersCount(): number {
  return getFromLocal().filter(o => o.status === 'pending' || o.status === 'preparing').length;
}
